import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { DialogTitle, useFeatureFlagShortcut, useFeatureFlags, useGetByDevice } from '@onesource/components';
import { useGetMyFeatureFlagsQuery } from '@onesource/services';
import { FC } from 'react';
import { MdCheck } from 'react-icons/md';
import { TbFlag } from 'react-icons/tb';

import { MembersFeatureFlagsDialogSkeleton } from './members-feature-flags-dialog-skeleton';

/**
 * Members-app Feature Flags dialog. Unlike the shared dev-tools dialog (which
 * toggles browser-local flags), the "App features" section is backed by the DB
 * and lists only flags the user has been **granted access to** by an admin.
 * Access is auto-enabled and not user-controllable, so this list is read-only —
 * a granted flag is active unless an admin has turned it off globally. The local
 * dev toggles are kept below so nothing regresses.
 *
 * Opens via the same Alt+Shift+/ shortcut. Mount this INSIDE the auth provider
 * so its queries carry a token; it reads open/close from the shared context.
 */
export const MembersFeatureFlagsDialog: FC = () => {
  const { isMobile } = useGetByDevice();
  const { isDialogOpen, openDialog, closeDialog, flags, toggleFeature, allFeatureFlags } = useFeatureFlags();
  useFeatureFlagShortcut(openDialog);

  const { data: dbFlags = [], isLoading } = useGetMyFeatureFlagsQuery();

  return (
    <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle icon={<TbFlag />} onClose={closeDialog}>
        Feature Flags
      </DialogTitle>
      <DialogContent>
        <Box pt={2}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            App features · shared with your account
          </Typography>

          {isLoading ? (
            <MembersFeatureFlagsDialogSkeleton />
          ) : dbFlags.length === 0 ? (
            <Box py={2}>
              <Typography variant="body2" color="text.secondary">
                No feature flags have been shared with you yet.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0.5}>
              {dbFlags.map(flag => (
                <Box key={flag.id} display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {flag.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      display="block"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {flag.slug}
                    </Typography>
                    {flag.description ? (
                      <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {flag.description}
                      </Typography>
                    ) : null}
                  </Box>
                  <Chip
                    size="small"
                    color={flag.enabled ? 'success' : 'default'}
                    label={flag.enabled ? 'Active' : 'Off globally'}
                  />
                </Box>
              ))}
            </Stack>
          )}

          {allFeatureFlags.length > 0 ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Developer toggles · this browser only
              </Typography>
              <Stack spacing={0.5}>
                {allFeatureFlags.map(flag => (
                  <Box key={flag.key} display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {flag.label}
                      </Typography>
                      {flag.description ? (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {flag.description}
                        </Typography>
                      ) : null}
                    </Box>
                    <Switch
                      checked={Boolean(flags[flag.key])}
                      disabled={flag.disabled}
                      onChange={() => toggleFeature(flag.key)}
                    />
                  </Box>
                ))}
              </Stack>
            </>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} variant="contained" startIcon={<MdCheck />}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
